import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("If that email exists, a reset link has been sent.");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#050505] p-6 text-white">
      <div className="aurora opacity-50" />
      <div className="grain" />

      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="glass relative w-full max-w-md rounded-3xl p-10"
      >
        <Link to="/login" className="mb-6 inline-flex items-center gap-1 text-xs text-white/60 hover:text-white" data-testid="forgot-back-link">
          <ArrowLeft className="h-3 w-3" /> Back to login
        </Link>
        <div className="mb-6 flex h-10 w-10 items-center justify-center rounded-xl brand-bg">
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <h1 className="font-display text-3xl font-light tracking-tight">Reset your password</h1>
        <p className="mt-2 text-sm text-white/55">
          Enter the email on your account and we'll send you a reset link.
        </p>

        {sent ? (
          <div className="mt-8 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 text-sm text-emerald-200" data-testid="forgot-success">
            <CheckCircle2 className="mb-2 h-5 w-5" />
            If an account exists for <span className="text-white">{email}</span>, a reset link has been sent.
            <div className="mt-2 text-xs text-white/45">
              (Dev: emails are logged to the backend console.)
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-white/50">Email</Label>
              <Input
                type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-white/10 bg-black/40 text-white"
                data-testid="forgot-email"
              />
            </div>
            <Button type="submit" disabled={submitting} className="h-11 w-full rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="forgot-submit">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send reset link
            </Button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
